import React from 'react';
import { portalCopy } from '../copy';
import type { BusinessListingPreview, WeekHours } from '../types';
import {
  formatBusinessClassLabel,
  formatBusinessClassPrice,
  formatBusinessClassSchedule,
  isBusinessClassComplete,
} from '../utils/businessClasses';
import { PHONE_SAFE_AREA_TOP } from './Phone15Frame';

const PREVIEW_SCALE = 0.62;
const DESIGN_WIDTH = 390;

type AppListingPreviewProps = {
  preview: BusinessListingPreview;
};

function formatInstagram(value: string | null): string | null {
  if (!value?.trim()) return null;
  const handle = value.trim().replace(/^@/, '');
  return `@${handle}`;
}

function formatWebsite(value: string | null): string | null {
  if (!value?.trim()) return null;
  return value.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <path d="M2 2l10 10M12 2 2 12" stroke="#5a3158" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <circle cx="7" cy="7" r="5.5" stroke="#EB42B2" strokeWidth="1.2" />
    <path d="M7 4.5V7.5L8.8 8.8" stroke="#EB42B2" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M3 7.5 6 4.5 9 7.5" stroke="#5a3158" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const HOURS_DAY_ORDER: (keyof WeekHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const HOURS_DAY_SHORT: Record<keyof WeekHours, string> = {
  mon: 'Lun',
  tue: 'Mar',
  wed: 'Mié',
  thu: 'Jue',
  fri: 'Vie',
  sat: 'Sáb',
  sun: 'Dom',
};

type PreviewHoursExpandedProps = {
  hours: WeekHours;
  isOpenNow: boolean;
};

const PreviewHoursExpanded: React.FC<PreviewHoursExpandedProps> = ({ hours, isOpenNow }) => (
  <div className="rounded-2xl bg-chisme-secondary px-3 py-3">
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <ClockIcon />
        <span className="font-jakarta text-[12px] font-medium text-[#2a0f2a]">Horario de apertura</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={`rounded-full px-2 py-0.5 font-jakarta text-[10px] font-bold ${
            isOpenNow ? 'bg-[#3d9e5f] text-white' : 'bg-[#5a3158]/15 text-[#5a3158]'
          }`}
        >
          {isOpenNow ? 'Abierto' : 'Cerrado'}
        </span>
        <ChevronUpIcon />
      </div>
    </div>

    <ul className="mt-2 space-y-1.5 border-t border-[rgba(74,33,71,0.08)] pt-2">
      {HOURS_DAY_ORDER.map((day) => (
        <li key={day} className="flex items-center justify-between gap-3 font-jakarta text-[11px]">
          <span className="font-medium text-[#2a0f2a]">{HOURS_DAY_SHORT[day]}</span>
          <span className={hours[day].open ? 'text-[#5a3158]' : 'text-[#5a3158]/60'}>
            {hours[day].open
              ? `${hours[day].from} – ${hours[day].to}`
              : portalCopy.wizard.profile.closed}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const InstagramIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="1.5" y="1.5" width="9" height="9" rx="2.5" stroke="#2a0f2a" strokeWidth="1.1" />
    <circle cx="6" cy="6" r="2.2" stroke="#2a0f2a" strokeWidth="1.1" />
    <circle cx="8.6" cy="3.4" r="0.6" fill="#2a0f2a" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <circle cx="6" cy="6" r="4.5" stroke="#2a0f2a" strokeWidth="1.1" />
    <path d="M1.5 6h9M6 1.5c1.5 1.8 1.5 7.2 0 9M6 1.5c-1.5 1.8-1.5 7.2 0 9" stroke="#2a0f2a" strokeWidth="1.1" />
  </svg>
);

const AppListingPreview: React.FC<AppListingPreviewProps> = ({ preview }) => {
  const name = preview.name ?? 'Tu negocio';
  const address = preview.address ?? 'Tu dirección';
  const description =
    preview.description ??
    'Describe tu local para que las usuarias sepan qué esperar antes de visitarte.';
  const instagram = formatInstagram(preview.instagram);
  const website = formatWebsite(preview.website);
  const instagramIsPlaceholder = !instagram;
  const websiteIsPlaceholder = !website;
  const instagramDisplay = instagram ?? portalCopy.preview.instagramPlaceholder;
  const websiteDisplay = website ?? portalCopy.preview.websitePlaceholder;
  const heroPhoto = preview.photoPreviewUrls[0];
  const photoTotal = Math.max(preview.photoCount, preview.photoPreviewUrls.length, 1);
  const classRows = preview.classes.filter(isBusinessClassComplete);
  const isWellness = preview.category === 'wellness';
  const showClasses = isWellness && classRows.length > 0;
  const showClassesPlaceholder = isWellness && classRows.length === 0;
  const heroHeight = DESIGN_WIDTH * (3 / 4) + PHONE_SAFE_AREA_TOP;

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden bg-[#fcf7fa] scrollbar-hide">
      <div
        className="origin-top-left bg-[#fcf7fa]"
        style={{
          width: DESIGN_WIDTH,
          transform: `scale(${PREVIEW_SCALE})`,
          transformOrigin: 'top left',
        }}
      >
        <div className="pb-6">
          {/* Hero — taller by safe-area inset; photo extends under status bar */}
          <div
            className="relative w-full overflow-hidden bg-[#e8d5e4]"
            style={{ height: heroHeight }}
          >
            {heroPhoto ? (
              <img src={heroPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#f2d9ec] to-[#e8c4df]">
                <span className="mt-6 font-jakarta text-sm text-[#5a3158]/50">Foto del local</span>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/25" />

            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              className="absolute right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow-sm"
              style={{ top: PHONE_SAFE_AREA_TOP - 6 }}
            >
              <CloseIcon />
            </button>

            <span className="absolute bottom-3 left-3 rounded-full bg-black/45 px-2.5 py-1 font-jakarta text-[11px] font-medium text-white backdrop-blur-sm">
              {preview.categoryLabel}
            </span>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1">
              {Array.from({ length: Math.min(photoTotal, 5) }).map((_, index) => (
                <span
                  key={index}
                  className={`h-1.5 w-1.5 rounded-full ${index === 0 ? 'bg-chisme-primary' : 'bg-white/80'}`}
                />
              ))}
            </div>

            <span className="absolute bottom-3 right-3 rounded-full bg-black/45 px-2 py-1 font-jakarta text-[11px] font-medium text-white backdrop-blur-sm">
              1/{photoTotal}
            </span>
          </div>

          {/* Info */}
          <div className="space-y-3 px-4 pt-4">
            <div>
              <h3 className="font-jakarta text-[22px] font-bold leading-tight text-[#2a0f2a]">{name}</h3>
              <p className="mt-0.5 font-jakarta text-[13px] text-[#5a3158]">{address}</p>
            </div>

            <p className="font-jakarta text-[13px] leading-relaxed text-[#2a0f2a]">{description}</p>

            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full bg-chisme-secondary px-3 py-2 font-jakarta text-[11px] font-medium ${
                  instagramIsPlaceholder ? 'text-[#5a3158]/50 italic' : 'text-[#2a0f2a]'
                }`}
              >
                <InstagramIcon />
                {instagramDisplay}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full bg-chisme-secondary px-3 py-2 font-jakarta text-[11px] font-medium ${
                  websiteIsPlaceholder ? 'text-[#5a3158]/50 italic' : 'text-[#2a0f2a]'
                }`}
              >
                <GlobeIcon />
                {websiteDisplay}
              </span>
            </div>

            {(showClasses || showClassesPlaceholder) && (
              <div>
                <h4 className="font-jakarta text-[15px] font-bold text-[#2a0f2a]">
                  {portalCopy.preview.classesTitle}
                </h4>
                {showClasses ? (
                  <div className="mt-2 space-y-2">
                    {classRows.map((row) => {
                      const price = formatBusinessClassPrice(row.price);
                      const schedule = formatBusinessClassSchedule(row);
                      return (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 rounded-2xl bg-chisme-secondary px-3 py-3"
                        >
                          <div className="flex min-w-0 items-start gap-2">
                            <span className="mt-0.5 shrink-0">
                              <ClockIcon />
                            </span>
                            <div className="min-w-0">
                              <span className="block font-jakarta text-[12px] font-semibold text-[#2a0f2a]">
                                {row.name.trim() || formatBusinessClassLabel(row)}
                              </span>
                              {schedule && (
                                <span className="mt-0.5 block font-jakarta text-[11px] text-[#5a3158]">
                                  {schedule}
                                </span>
                              )}
                            </div>
                          </div>
                          {price && (
                            <span className="shrink-0 font-jakarta text-[12px] font-bold text-chisme-primary">
                              {price}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {portalCopy.preview.classPlaceholders.map((placeholder) => (
                      <div
                        key={placeholder.name}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-[rgba(74,33,71,0.15)] bg-chisme-secondary/50 px-3 py-3"
                      >
                        <div className="flex min-w-0 items-start gap-2">
                          <span className="mt-0.5 shrink-0 opacity-40">
                            <ClockIcon />
                          </span>
                          <div className="min-w-0">
                            <span className="block font-jakarta text-[12px] font-medium italic text-[#5a3158]/55">
                              {placeholder.name}
                            </span>
                            <span className="mt-0.5 block font-jakarta text-[11px] italic text-[#5a3158]/45">
                              {placeholder.schedule}
                            </span>
                          </div>
                        </div>
                        <span className="shrink-0 font-jakarta text-[12px] font-bold italic text-chisme-primary/40">
                          {placeholder.price}€
                        </span>
                      </div>
                    ))}
                    <p className="pt-0.5 text-center font-jakarta text-[10px] italic text-[#5a3158]/55">
                      {portalCopy.preview.classesEmptyHint}
                    </p>
                  </div>
                )}
              </div>
            )}

            {preview.hoursEnabled && preview.hours && (
              <PreviewHoursExpanded hours={preview.hours} isOpenNow={preview.isOpenNow} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppListingPreview;
